import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, AlertTriangle, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import LSSDLogo from "../components/LSSDLogo";
import { consumePostLoginRedirect } from "../lib/postLoginRedirect";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

/**
 * CompletaProfilo — completa i dati IC dopo Google Sign-In.
 *
 * Due modalità:
 * 1. Nuovo utente Google: legge `google_pending_token` da sessionStorage.
 *    Il record utente NON esiste ancora in DB — verrà creato atomicamente al submit.
 * 2. Utente Google legacy: sessione già attiva (cookies), profilo vuoto → completa in-place.
 */
export default function CompletaProfilo() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ rp_name: "", discord_id: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingName, setPendingName] = useState("");

  useEffect(() => {
    let tok = "";
    let em = "";
    let nm = "";
    try {
      tok = sessionStorage.getItem("google_pending_token") || "";
      em = sessionStorage.getItem("google_pending_email") || "";
      nm = sessionStorage.getItem("google_pending_name") || "";
    } catch (err) { console.debug("[completa-profilo] sessionStorage read failed", err); }
    setPendingToken(tok);
    setPendingEmail(em);
    setPendingName(nm);

    // Redirect a login solo se non abbiamo NÉ pending token NÉ sessione utente.
    if (!tok && !user) {
      nav("/login", { replace: true });
      return;
    }
    if (user && !user.google_auth) {
      nav(consumePostLoginRedirect(), { replace: true });
      return;
    }
    if (user?.rp_name && user?.discord_id) {
      nav("/registrazione-in-attesa", { replace: true });
    }
  }, [user, nav]);

  const clearPending = () => {
    try {
      sessionStorage.removeItem("google_pending_token");
      sessionStorage.removeItem("google_pending_email");
      sessionStorage.removeItem("google_pending_name");
    } catch (err) { console.debug("[completa-profilo] sessionStorage clear failed", err); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const body = pendingToken
        ? { ...form, pending_token: pendingToken }
        : form;
      await api.post("/auth/google/complete-profile", body);
      clearPending();
      try { await refresh?.(); } catch (err) { console.debug("[completa-profilo] refresh failed", err); }
      nav("/registrazione-in-attesa", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiErrorDetail(detail) || "Errore invio profilo";
      setError(msg);
      // Se il token è scaduto, invalida sessione locale e rimanda al login
      if (typeof detail === "string" && detail.toLowerCase().includes("scaduta")) {
        clearPending();
        setTimeout(() => nav("/login", { replace: true }), 2500);
      }
    } finally {
      setLoading(false);
    }
  };

  const displayEmail = pendingEmail || user?.email || "";

  return (
    <div className="min-h-screen bg-[#0A1F18] flex items-center justify-center px-4 py-12" data-testid="complete-profile-page">
      <div className="w-full max-w-md">
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-8 sm:p-10">
          <div className="flex flex-col items-center text-center mb-8">
            <LSSDLogo className="h-20 w-20 mb-4" />
            <div className="uppercase-label text-sheriff-gold">Google Sign-In · Passo Finale</div>
            <h1 className="font-heading font-black text-3xl tracking-tighter text-[#F5E6B8] mt-1">Completa il Profilo</h1>
            <p className="text-[#B8A269] text-sm mt-2">
              Inserisci Nome e Cognome IC e il tuo ID Discord per completare la richiesta di accesso.
              {pendingName && <span className="block mt-1 text-xs text-[#8B7B49]">Google: {pendingName}</span>}
              {displayEmail && <span className="block mt-0.5 text-xs text-[#8B7B49]">{displayEmail}</span>}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-sheriff-gold/10 border border-sheriff-gold/40 rounded-xl text-sm text-[#F5E6B8]">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-sheriff-gold" />
              <span>
                <span className="font-semibold text-sheriff-gold">Attenzione:</span> inserisci dati reali.
                La richiesta viene creata solo dopo il completamento di questo modulo.
                Le informazioni fittizie invalideranno l&apos;account.
              </span>
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#E8D89A] mb-1.5 block">Nome e Cognome RP</Label>
              <Input
                data-testid="complete-rp-name"
                required
                minLength={2}
                value={form.rp_name}
                onChange={(e) => setForm({ ...form, rp_name: e.target.value })}
                placeholder="John Marston"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#E8D89A] mb-1.5 block">
                ID Discord
                <span className="ml-1 text-[10px] text-[#8B7B49] font-normal">(sincronizzazione grado)</span>
              </Label>
              <Input
                data-testid="complete-discord-id"
                type="text"
                required
                pattern="\d{15,25}"
                inputMode="numeric"
                value={form.discord_id}
                onChange={(e) => setForm({ ...form, discord_id: e.target.value.replace(/\D/g, "") })}
                placeholder="es. 351876452198482432"
              />
              <p className="text-[10px] text-[#8B7B49] mt-1.5 leading-snug">
                Su Discord: <span className="text-sheriff-gold">Impostazioni → Avanzate → Modalità sviluppatore</span>. Poi click destro sul tuo nome → <span className="text-sheriff-gold">Copia ID utente</span>.
              </p>
            </div>

            {error && (
              <div data-testid="complete-error" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="complete-submit"
              className="w-full bg-sheriff-green hover:bg-sheriff-green-dark text-white rounded-full uppercase-label font-bold h-11"
            >
              {loading ? "Invio…" : (<><UserPlus className="h-4 w-4 mr-2" /> Completa Registrazione</>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
